<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useFunnelManager } from 'dashboard/composables/kanban/useFunnelManager';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import { DATE_PICKER_LANG_PT_BR } from 'dashboard/helper/datePickerLang';

const props = defineProps({
  filters: {
    type: Object,
    required: true,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['filterChange']);

const store = useStore();

// Usar composable de funnels
const { funnels: funnelsList, loadFunnels } = useFunnelManager();

// Inicializar datas padrão (últimos 30 dias)
const getDefaultDates = () => {
  const now = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(now.getDate() - 30);
  thirtyDaysAgo.setHours(0, 0, 0, 0);
  now.setHours(23, 59, 59, 999);

  return {
    since: thirtyDaysAgo.getTime(),
    until: now.getTime(),
  };
};

const defaultDates = getDefaultDates();

// Estado local dos filtros
const localFilters = ref({
  since: props.filters.since || defaultDates.since,
  until: props.filters.until || defaultDates.until,
  funnel_ids: [...(props.filters.funnel_ids || [])],
  agent_ids: [...(props.filters.agent_ids || [])],
  product_ids: [...(props.filters.product_ids || [])],
  min_value: props.filters.min_value,
  max_value: props.filters.max_value,
});

// Getters
const agents = useMapGetter('agents/getAgents');

// Computed
const funnelOptions = computed(() => {
  return (funnelsList.value || []).map(funnel => ({
    id: funnel.id,
    name: funnel.name,
    icon: funnel.settings?.icon || '🔽',
    color: funnel.settings?.color || '#1F93FF',
  }));
});

const agentOptions = computed(() => {
  return (agents.value || []).map(agent => ({
    id: agent.id,
    name: agent.name,
    email: agent.email,
    thumbnail: agent.thumbnail,
  }));
});

const selectedFunnels = computed({
  get: () =>
    funnelOptions.value.filter(f =>
      localFilters.value.funnel_ids.includes(f.id)
    ),
  set: value => {
    localFilters.value.funnel_ids = value ? value.map(f => f.id) : [];
  },
});

const selectedAgents = computed({
  get: () =>
    agentOptions.value.filter(a => localFilters.value.agent_ids.includes(a.id)),
  set: value => {
    localFilters.value.agent_ids = value ? value.map(a => a.id) : [];
  },
});

// Removido: handlers separados - v-model já atualiza automaticamente

const dateRangeValue = computed(() => {
  return [
    new Date(localFilters.value.since),
    new Date(localFilters.value.until),
  ];
});

// Métodos
const applyFilters = () => {
  emit('filterChange', { ...localFilters.value });
};

// Funil e agente aplicam na hora, sem botão "Aplicar".
watch(
  () => [localFilters.value.funnel_ids, localFilters.value.agent_ids],
  () => applyFilters(),
  { deep: true }
);

const onDateRangeChange = value => {
  if (value && value.length === 2) {
    const since = new Date(value[0]);
    const until = new Date(value[1]);

    since.setHours(0, 0, 0, 0);
    until.setHours(23, 59, 59, 999);

    localFilters.value.since = since.getTime();
    localFilters.value.until = until.getTime();
    applyFilters();
  }
};

const clearFilters = () => {
  const dates = getDefaultDates();

  localFilters.value = {
    since: dates.since,
    until: dates.until,
    funnel_ids: [],
    agent_ids: [],
    product_ids: [],
    min_value: null,
    max_value: null,
  };

  applyFilters();
};

// Lifecycle
onMounted(async () => {
  // Carregar funnels usando o composable
  await loadFunnels();

  // Carregar agentes se necessário
  if (!agents.value || agents.value.length === 0) {
    store.dispatch('agents/get');
  }
});
</script>

<template>
  <div
    class="bg-white dark:bg-n-slate-2 border border-n-slate-6 rounded-lg p-4"
  >
    <div class="flex flex-col gap-4">
      <!-- Linha 1: Data Range -->
      <div class="flex items-center gap-4">
        <div class="flex-1">
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ $t('KANBAN_REPORTS.FILTERS.DATE_RANGE') }}
          </label>
          <WootDateRangePicker
            :value="dateRangeValue"
            show-range
            :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
            :placeholder="$t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
            :lang="DATE_PICKER_LANG_PT_BR"
            @change="onDateRangeChange"
          />
        </div>
      </div>

      <!-- Linha 2: Filtros principais -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <!-- Filtro de Funis -->
        <div class="multiselect-wrap--small">
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ $t('KANBAN_REPORTS.FILTERS.FUNNELS') }}
          </label>
          <multiselect
            v-model="selectedFunnels"
            :options="funnelOptions"
            :placeholder="$t('KANBAN_REPORTS.FILTERS.SELECT_FUNNELS')"
            label="name"
            track-by="id"
            multiple
            :max-height="160"
            selected-label
            :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
            deselect-label=""
          >
            <template #noOptions>
              {{ $t('KANBAN_REPORTS.FILTERS.NO_FUNNELS_FOUND') }}
            </template>
          </multiselect>
        </div>

        <!-- Filtro de Agentes -->
        <div class="multiselect-wrap--small">
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ $t('KANBAN_REPORTS.FILTERS.AGENTS') }}
          </label>
          <multiselect
            v-model="selectedAgents"
            :options="agentOptions"
            :placeholder="$t('KANBAN_REPORTS.FILTERS.SELECT_AGENTS')"
            label="name"
            track-by="id"
            multiple
            :max-height="160"
            selected-label
            :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
            deselect-label=""
          >
            <template #noOptions>
              {{ $t('KANBAN_REPORTS.FILTERS.NO_AGENTS_FOUND') }}
            </template>
          </multiselect>
        </div>
      </div>

      <!-- Botões -->
      <div class="flex justify-end gap-2">
        <button
          class="px-4 py-2 text-sm font-medium text-n-slate-11 hover:text-n-slate-12 transition-colors"
          :disabled="isLoading"
          @click="clearFilters"
        >
          {{ $t('KANBAN_REPORTS.FILTERS.CLEAR') }}
        </button>
      </div>
    </div>
  </div>
</template>
